import React from "react";
import { useLocation } from "react-router-dom";
import { Tree, TreeItem, TreeItemLayout, Text } from "@fluentui/react-components";

export const TREE_PAGE_SIZE = 20;

export interface PaginatedTreeItem {
    key: string;
    label?: string;
    path: string;
    /** Other routes showing the same item (e.g. its edit page) that also mark it as selected. */
    activePaths?: string[];
}

export interface PaginatedTreeGroupProps {
    value: string;
    title: string;
    items: PaginatedTreeItem[];
    page: number;
    onPageChange: (page: number) => void;
    colorClass: string;
    emptyText: string;
    searchActive: boolean;
    onNavigate: (path: string) => void;
}

export function getVisiblePages(totalPages: number, currentPage: number, maxVisible = 3): number[] {
    if (totalPages <= 0) return [];

    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, currentPage - half);
    const end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function paginate<T>(items: T[], page: number, pageSize = TREE_PAGE_SIZE): T[] {
    const startIndex = (page - 1) * pageSize;
    return items.slice(startIndex, startIndex + pageSize);
}

export function clampPage(page: number, totalPages: number): number {
    return Math.min(Math.max(1, page), totalPages);
}

export default function PaginatedTreeGroup({
    value,
    title,
    items,
    page,
    onPageChange,
    colorClass,
    emptyText,
    searchActive,
    onNavigate,
}: PaginatedTreeGroupProps) {
    const { pathname } = useLocation();
    const totalPages = Math.max(1, Math.ceil(items.length / TREE_PAGE_SIZE));
    const currentPage = clampPage(page, totalPages);
    const pageItems = paginate(items, currentPage);
    const navButtonClass = (disabled: boolean) =>
        `px-3 py-1 ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50 font-bold"}`;

    return (
        <TreeItem value={value} itemType="branch">
            <TreeItemLayout>
                <Text>{`${title} (${items.length})`}</Text>
            </TreeItemLayout>
            <Tree>
                {pageItems.length > 0 ? pageItems.map(item => (
                    <TreeItem key={item.key} itemType="leaf">
                        <TreeItemLayout className={colorClass} onClick={() => onNavigate(item.path)}>
                            <span style={{ textDecoration: pathname === item.path || item.activePaths?.includes(pathname) ? "underline" : "none" }}>
                                {item.label}
                            </span>
                        </TreeItemLayout>
                    </TreeItem>
                )) : (
                    <TreeItem key={`no-${value}`} itemType="leaf">
                        <TreeItemLayout><Text>{searchActive ? `${emptyText} for your search` : emptyText}</Text></TreeItemLayout>
                    </TreeItem>
                )}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-1 mt-2 mb-4 flex-wrap">
                        <button
                            type="button"
                            aria-label="Previous page"
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={navButtonClass(currentPage === 1)}
                        >
                            &lt;
                        </button>
                        {getVisiblePages(totalPages, currentPage).map(visiblePage => (
                            <button
                                type="button"
                                key={visiblePage}
                                aria-label={`Page ${visiblePage}`}
                                aria-current={visiblePage === currentPage ? "page" : undefined}
                                onClick={() => onPageChange(visiblePage)}
                                className={`px-3 py-1 ${visiblePage === currentPage ? "bg-blue-500 text-white font-bold" : "text-gray-700 hover:bg-gray-50"}`}
                            >
                                {visiblePage}
                            </button>
                        ))}
                        <button
                            type="button"
                            aria-label="Next page"
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={navButtonClass(currentPage === totalPages)}
                        >
                            &gt;
                        </button>
                    </div>
                )}
            </Tree>
        </TreeItem>
    );
}
